import { IVirtualMachinesSelector } from './newModel'

export const convertVMFilterParamsToQueryParams = (searchParams) => {
  if (!searchParams) {
    return ''
  }

  let query_params = ''
  Object.entries(searchParams)?.forEach(([key, value]) => {
    if (key === 'tenant_id') {
      return
    }

    if (key && value && value !== 'all') {
      if (key === 'user_id') {
        query_params += `&owner=${value}`
      } else {
        query_params += `&${key}=${value}`
      }
    }
  })
  return query_params
}

/**
 * Util function to check if the VM is using only simple network
 * Simple network or L2-only network VMs don't have addresses
 * @param vm
 * @returns true if the VM is using only simple network
 */
export const isVMUsingOnlySimpleNetwork = (vm: IVirtualMachinesSelector) => {
  return Object.values(vm?.addresses || {}).flatMap((ip) => ip)?.length === 0
}

/**
 * One network-interface IP/MAC pair that the IP-discovery agent found for an
 * L2 (non-Neutron-managed) port on a VM. [FR-011]
 */
export interface DiscoveredPortBinding {
  portId: string
  mac: string
  ip: string
}

/**
 * The IP-discovery agent (pf9-ip-discovery) records the IP it finds for each L2
 * port directly on the VM's server metadata. It uses the port ID as the key and
 * a small JSON string as the value, e.g.:
 *   metadata["<port-id>"] = '{"mac":"fa:16:3e:..","ip":"192.168.1.10"}'
 * There is one such entry per network interface. Plain user metadata
 * (e.g. HOTPLUG_MEMORY) is not JSON shaped like this, so it is ignored. [FR-011]
 */
const parsePortBinding = (rawValue: unknown): { mac: string; ip: string } | null => {
  if (typeof rawValue !== 'string') return null
  try {
    const parsed = JSON.parse(rawValue)
    if (parsed && typeof parsed === 'object' && 'mac' in parsed && 'ip' in parsed) {
      return { mac: String(parsed.mac ?? ''), ip: String(parsed.ip ?? '') }
    }
  } catch {
    // Not a JSON-encoded port binding — treat it as ordinary user metadata.
  }
  return null
}

/**
 * Pull the discovered L2 IP/MAC bindings out of a VM's server metadata. Returns
 * one entry per network interface that has a discovered IP. [FR-011]
 */
export const getDiscoveredPortBindings = (
  metadata: Record<string, string> | undefined,
): DiscoveredPortBinding[] => {
  if (!metadata) return []
  return Object.entries(metadata).reduce<DiscoveredPortBinding[]>((accum, [portId, rawValue]) => {
    const binding = parsePortBinding(rawValue)
    if (binding?.ip) {
      accum.push({ portId, mac: binding.mac, ip: binding.ip })
    }
    return accum
  }, [])
}

/**
 * The result of attributing a VM's discovered L2 IPs to networks. `resolved` maps a
 * network *name* (matching the keys of the VM's `addresses` object) to the IPs on it.
 * `unresolved` holds IPs we could not attribute — e.g. the VM has no matching port, or
 * the port doesn't carry a network name. [FR-035]
 */
export interface GroupedL2Ips {
  resolved: Record<string, string[]>
  unresolved: string[]
}

/**
 * Group a VM's discovered L2 IPs by their network name so each IP can render under the
 * correct label in the list. Joins two sources on the VM itself:
 *   metadata (portId -> ip)  +  vm.ports (portId -> network_name)
 * Each port carries its own network name, so no separate networks lookup is needed. An IP
 * whose port is missing or has no network name falls into `unresolved` for the caller to
 * render via its fallback path. [FR-035]
 */
export const groupL2IpsByNetworkName = (vm: {
  metadata?: Record<string, string>
  ports?: Array<{ id: string; network_name?: string }>
}): GroupedL2Ips => {
  const resolved: Record<string, string[]> = {}
  const unresolved: string[] = []
  const portToNetworkName = new Map((vm?.ports || []).map((port) => [port.id, port.network_name]))

  for (const binding of getDiscoveredPortBindings(vm?.metadata)) {
    const networkName = portToNetworkName.get(binding.portId)
    if (networkName) {
      if (!resolved[networkName]) resolved[networkName] = []
      resolved[networkName].push(binding.ip)
    } else {
      unresolved.push(binding.ip)
    }
  }

  return { resolved, unresolved }
}

/**
 * Return VM metadata with the IP-discovery port bindings removed, so only real
 * user metadata is shown as key=value labels (the port bindings are surfaced as
 * IP addresses elsewhere, not as raw labels). [FR-024]
 */
export const stripDiscoveredPortBindings = (
  metadata: Record<string, string> | undefined,
): Record<string, string> => {
  if (!metadata) return {}
  return Object.entries(metadata).reduce<Record<string, string>>((accum, [key, rawValue]) => {
    if (parsePortBinding(rawValue) === null) {
      accum[key] = rawValue
    }
    return accum
  }, {})
}
